import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonsWrapper,
  DeleteModal,
  ICONS,
  PageLayout,
  PLATFORM_THEME_COLORS,
  SearchInput,
  Typography,
  TYPOGRAPHY_SIZE,
  EMPTY_ILLUSTRATIONS,
  EmptyContainer,
  showNotification,
  NOTIFICATION_TYPE,
} from '@thinknet-gmbh/platform-common-ui';
import {
  PLATFORM_PERMISSIONS,
  PERMISSION_ACTIONS,
  ProjectStatusesEnum,
} from '@thinknet-gmbh/platform-common';
import { useGetAllCustomers } from 'src/hooks/customers/get-customers.hook';
import { useDeleteCustomer } from 'src/hooks/customers/delete-customer.hook';
import PermissionCheck from 'src/components/permission-check';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useGetCustomerProjectsCount } from 'src/hooks/projects/get-customer-projects-count.hook';
import { useGetKWPConnectionByUserId } from 'src/hooks/interfaces/kwp/get-kwp-connection.hook';
import { useGetCurrentAccount } from 'src/hooks/accounts/get-current-account.hook';
import { useCheckIfTokenIsValid } from 'src/hooks/interfaces/kwp/check-token-validation.hook';
import { HeaderContainer } from './customers.styles';
import { CustomersListTable } from './components/customers-list-table';
import { CUSTOMERS_MODALS } from './customers.constants';
import { CreateCustomerModal } from './components/create-customer-modal';
import { KwpFetchingDataModal } from '../settings/components/interfaces/kwp/components/modals/kwp-fetching-data-modal-component';

const Customer: React.FC = () => {
  const [searchValue, setSearchValue] = useState('');
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [deleteCustomerData, setDeleteCustomerData] = useState<any>();

  const { t } = useTranslation('customers');

  const onSuccess = () => {
    setActiveModal(null);
    setDeleteCustomerData(null);
  };
  const { account } = useGetCurrentAccount();
  const deleteCustomer = useDeleteCustomer({ onSuccess });
  const currentCompanyId = useGetCurrentCompanyId();
  const { getCustomers, paginationConfig, onTableChange } = useGetAllCustomers(
    currentCompanyId,
    searchValue
  );
  const { totalCount, loading } = useGetCustomerProjectsCount(deleteCustomerData?._id, [
    ProjectStatusesEnum.ACTIVE,
  ]);
  const { interfaceConnection } = useGetKWPConnectionByUserId(account._id);
  const { isTokenValid } = useCheckIfTokenIsValid(interfaceConnection?.link);

  const onSearchValueChange = (e: any) => {
    setSearchValue(e.target.value);
    paginationConfig.onChange(1);
  };

  const onDeleteSubmit = () => {
    if (!loading) {
      if (totalCount) {
        return showNotification({
          type: NOTIFICATION_TYPE.error,
          message: t('notifications.customerCannotBeDeleted'),
          description: t('notifications.customerCannotBeDeletedSubTitle'),
        });
      }

      deleteCustomer({
        variables: { customerId: deleteCustomerData._id, companyId: currentCompanyId },
      });
    }

    if (getCustomers.length <= 1) {
      paginationConfig.onChange(paginationConfig.current - 1);
    }
  };

  return (
    <PageLayout pageTitle={t('labels.customer')}>
      {activeModal === CUSTOMERS_MODALS.createModal && (
        <CreateCustomerModal onClose={() => setActiveModal(null)} />
      )}
      {activeModal === CUSTOMERS_MODALS.kwpCustomers && (
        <KwpFetchingDataModal
          onClose={() => setActiveModal(null)}
          userLink={interfaceConnection?.link}
          activeModal={activeModal}
        />
      )}
      {activeModal === CUSTOMERS_MODALS.deleteModal && (
        <DeleteModal
          translate={t}
          isOpened
          title={t('deleteModal.title')}
          subTitle={t('deleteModal.subtitle', {
            customerName: deleteCustomerData?.name,
          })}
          onSubmit={onDeleteSubmit}
          onClose={() => setActiveModal(null)}
        />
      )}
      <HeaderContainer>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('labels.customer')}
        </Typography>
        <ButtonsWrapper>
          <SearchInput value={searchValue} onChange={onSearchValueChange} />
          <PermissionCheck
            permission={PLATFORM_PERMISSIONS.customerManagement}
            action={PERMISSION_ACTIONS.create}
          >
            <Button
              type="primary"
              ghost
              icon={ICONS.link}
              iconColor={PLATFORM_THEME_COLORS.primary.blue}
              disabled={!isTokenValid}
              onClick={() => {
                setActiveModal(CUSTOMERS_MODALS.kwpCustomers);
              }}
            >
              {t('buttons.syncWithKWP')}
            </Button>
            <Button
              type="primary"
              icon={ICONS.plus}
              iconColor={PLATFORM_THEME_COLORS.primary.white}
              onClick={() => setActiveModal(CUSTOMERS_MODALS.createModal)}
            >
              {t('buttons.createCustomer')}
            </Button>
          </PermissionCheck>
        </ButtonsWrapper>
      </HeaderContainer>
      {getCustomers.length > 0 ? (
        <CustomersListTable
          data={getCustomers}
          openDelete={record => {
            setActiveModal(CUSTOMERS_MODALS.deleteModal);
            setDeleteCustomerData(record);
          }}
          pagination={paginationConfig}
          onChange={onTableChange}
        />
      ) : (
        <EmptyContainer
          illustration={EMPTY_ILLUSTRATIONS.seo}
          emptyTitle={t('common:emptySearch.title')}
          contentText={t('common:emptySearch.subTitle')}
        />
      )}
    </PageLayout>
  );
};

export default Customer;
